/**
 * Compact tag row stored under a UUID key in memory / IndexedDB.
 * @typedef {{ tag_name: string, group_name: string | null, color_code: string, pinned?: boolean, deleted?: boolean }} TagsCatalogRecord
 */

/**
 * Expanded tag metadata returned to UI consumers.
 * @typedef {TagsCatalogRecord & { id: string, tag_id: string, unknown?: boolean }} TagMeta
 */

export const toTagId = (raw) => String(raw?.id ?? raw?.tag_id ?? '').trim()

const hasIncomingCatalogFields = (raw) => {
  if (!raw || typeof raw !== 'object') return false

  return (
    raw.tag_name != null ||
    raw.name != null ||
    raw.label != null ||
    raw.color_code != null ||
    raw.color != null ||
    raw.group_name != null ||
    raw.pinned != null ||
    raw.deleted != null
  )
}

/**
 * Normalize any inbound tag shape into the compact stored record (id is the map key).
 * When `existing` is provided, partial realtime UPDATE payloads merge into the stored row.
 * @param {object | null | undefined} raw
 * @param {TagsCatalogRecord | null | undefined} [existing]
 * @returns {TagsCatalogRecord | null} Compact stored catalog row, merged when partial.
 */
export function toCatalogRecord(raw, existing = null) {
  if (!raw && !existing) return null

  if (!hasIncomingCatalogFields(raw)) {
    return existing ?? null
  }

  const tagName = raw?.tag_name ?? raw?.name ?? raw?.label
  const colorCode = raw?.color_code ?? raw?.color

  return {
    tag_name: tagName ?? existing?.tag_name ?? 'Unknown tag',
    group_name:
      raw?.group_name !== undefined ? (raw.group_name ?? null) : (existing?.group_name ?? null),
    color_code: colorCode ?? existing?.color_code ?? '#000000',
    ...(raw?.pinned != null
      ? { pinned: Boolean(raw.pinned) }
      : existing?.pinned != null
        ? { pinned: existing.pinned }
        : {}),
    ...(raw?.deleted != null
      ? { deleted: Boolean(raw.deleted) }
      : existing?.deleted != null
        ? { deleted: existing.deleted }
        : {}),
  }
}

/**
 * Expand a stored record into the legacy TagMeta shape expected by UI callers.
 * @param {string} tagId
 * @param {TagsCatalogRecord | null | undefined} record
 * @returns {TagMeta | null} Expanded tag metadata for UI consumers.
 */
export function toTagMeta(tagId, record) {
  const id = String(tagId ?? '').trim()
  if (!id || !record) return null

  return {
    id,
    tag_id: id,
    tag_name: record.tag_name,
    group_name: record.group_name,
    color_code: record.color_code,
    ...(record.pinned != null ? { pinned: record.pinned } : {}),
    ...(record.deleted != null ? { deleted: record.deleted } : {}),
  }
}

/**
 * @param {object | null | undefined} raw
 * @returns {TagMeta | null} Normalized tag metadata from any inbound shape.
 */
export function normalizeCatalogTag(raw) {
  const id = toTagId(raw)
  const record = toCatalogRecord(raw)
  if (!id || !record) return null
  return toTagMeta(id, record)
}

export function createUnknownCatalogTag(tagId) {
  const id = String(tagId ?? '').trim()
  return {
    id,
    tag_id: id,
    tag_name: id || 'Unknown tag',
    group_name: null,
    color_code: '#9CA3AF',
    unknown: true,
  }
}

/**
 * @param {Array<object>} tags
 * @returns {Record<string, TagsCatalogRecord>} Id-keyed catalog map from a tag array.
 */
export function tagsArrayToById(tags = []) {
  const byId = {}
  tags.forEach((tag) => {
    const id = toTagId(tag)
    const record = toCatalogRecord(tag)
    if (!id || !record) return
    byId[id] = record
  })
  return byId
}

/**
 * @param {Record<string, TagsCatalogRecord> | null | undefined} tagsById
 * @returns {TagMeta[]} Flat tag list derived from an id-keyed catalog map.
 */
export function tagsByIdToList(tagsById = {}) {
  return Object.entries(tagsById || {})
    .map(([id, record]) => toTagMeta(id, record))
    .filter(Boolean)
}

/**
 * Accept legacy array payloads or id-keyed records from IndexedDB / network.
 * @param {Array<object> | Record<string, TagsCatalogRecord> | null | undefined} source
 * @returns {Record<string, TagsCatalogRecord>} Normalized id-keyed catalog map.
 */
export function normalizeTagsCatalogById(source) {
  if (!source) return {}
  if (Array.isArray(source)) return tagsArrayToById(source)
  if (typeof source !== 'object') return {}

  const byId = {}
  Object.entries(source).forEach(([id, record]) => {
    const tagId = String(id ?? '').trim()
    const normalized = toCatalogRecord(record)
    if (!tagId || !normalized) return
    byId[tagId] = normalized
  })
  return byId
}
